import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Widget from './Widget';
import { listBots } from '../../api/botsApi';

/**
 * ActiveBotsWidget - Shows bot overview with live run status.
 */
const ActiveBotsWidget = ({ hideTitle = false }) => {
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await listBots();
      const arr = Array.isArray(res) ? res : (res?.data ?? res?.results ?? []);
      setBots(arr);
    } catch (err) {
      console.error('ActiveBotsWidget fetch error:', err);
      setError('Failed to load bots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeBots = bots.filter((b) => b.is_active);
  const inactiveBots = bots.filter((b) => !b.is_active);

  return (
    <Widget title="Trading Bots" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {loading && !bots.length ? (
          <div className="flex-1 flex items-center justify-center text-textSecondary text-sm">Loading…</div>
        ) : error ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2">
            <span className="text-destructive text-sm">{error}</span>
            <button className="btn btn-neutral btn-xs" onClick={fetchData}>Retry</button>
          </div>
        ) : (
          <>
            {/* Counters */}
            <div className="px-3 py-2.5 border-b border-borderDefault flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-success" />
                <span className="text-sm font-semibold text-textPrimary">{activeBots.length}</span>
                <span className="text-xs text-textSecondary">Active</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-500" />
                <span className="text-sm font-semibold text-textPrimary">{inactiveBots.length}</span>
                <span className="text-xs text-textSecondary">Inactive</span>
              </div>
              <div className="ml-auto text-xs text-textSecondary">{bots.length} total</div>
            </div>

            {/* Bot list */}
            <div className="flex-1 overflow-y-auto">
              {bots.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2">
                  <span className="text-sm text-textSecondary">No bots created yet</span>
                  <button
                    className="btn btn-primary btn-xs"
                    onClick={() => navigate('/bots/create')}
                  >
                    Create Bot
                  </button>
                </div>
              ) : (
                <ul className="divide-y divide-borderDefault">
                  {bots.slice(0, 10).map((bot) => (
                    <li
                      key={bot.id}
                      className="px-3 py-2 flex items-center justify-between hover:bg-secondary/10 cursor-pointer transition"
                      onClick={() => navigate(`/bots/${bot.id}`)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`flex-shrink-0 w-2 h-2 rounded-full ${
                              bot.is_active ? 'bg-success' : 'bg-gray-500'
                            }`}
                          />
                          <span className="text-sm font-medium text-textPrimary truncate">
                            {bot.name}
                          </span>
                        </div>
                        <div className="ml-4 text-[10px] text-textSecondary truncate">
                          {bot.account?.name || bot.account_id || '—'}
                        </div>
                      </div>
                      <span className="text-[10px] text-textSecondary ml-2 whitespace-nowrap">
                        {bot.is_active ? 'Running' : 'Stopped'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Footer */}
            <div className="px-3 py-2 border-t border-borderDefault">
              <button
                className="text-xs text-primary hover:underline"
                onClick={() => navigate('/bots')}
              >
                Manage Bots →
              </button>
            </div>
          </>
        )}
      </div>
    </Widget>
  );
};

export default ActiveBotsWidget;
